/**
 * Q5 — "What should I fix first?" (SYR-68, Phase C).
 *
 * The single highest-leverage fix, driven entirely by the Briefing AI
 * `the_fix` output ({ title, redline, move, expectedEffect }). Matches the
 * prototype's FIX-1 card: red number block + body.
 */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Card } from '../atoms';
import { LockedOverlay } from '../atoms/LockedOverlay';
import { SectionHead } from './SectionHead';
import { BAND_PALETTE } from '../config/bandColours';
import { THEME } from '../config/theme';
import { useAskExpert } from '../askExpert';

export interface TheFix {
  title?: string;
  redline?: string;
  move?: string;
  expectedEffect?: string;
}

export interface TheFixCardProps {
  fix: TheFix | undefined;
  /** Tier-locked: blur the body behind an upgrade CTA (title stays visible). */
  locked?: boolean;
}

// Static placeholder shown (blurred) when the section is locked — the real
// the_fix AI isn't generated for restricted tiers, so this just suggests shape.
const PLACEHOLDER_FIX: TheFix = {
  title: 'Rebuild premium pricing power',
  redline: 'Willingness to pay trails the category and is dragging equity.',
  move: 'Reframe value messaging around the outcomes customers told you they value.',
  expectedEffect: 'Lifts perceived value and recovers margin without discounting.',
};

export const TheFixCard: React.FunctionComponent<TheFixCardProps> = ({ fix, locked = false }) => {
  const intl = useIntl();
  const askExpert = useAskExpert();
  // Locked tiers never generate the_fix — show the placeholder behind the blur.
  const effectiveFix = locked ? fix ?? PLACEHOLDER_FIX : fix;

  // Hand the fix to the Head of Brand Marketing as a ready-framed question.
  // The prompt lands in the chat composer editable, never auto-sent.
  const handleAskExpert = (): void => {
    if (!effectiveFix) return;
    const parts = [
      effectiveFix.title &&
        intl.formatMessage(
          {
            id: 'marketerView.q5fix.promptTitle',
            defaultMessage: '"{title}" is the top fix from my brand equity briefing.',
          },
          { title: effectiveFix.title },
        ),
      effectiveFix.redline,
      effectiveFix.move &&
        intl.formatMessage(
          { id: 'marketerView.q5fix.promptMove', defaultMessage: 'The suggested move is: {move}' },
          { move: effectiveFix.move },
        ),
      intl.formatMessage({
        id: 'marketerView.q5fix.promptAsk',
        defaultMessage: 'How should I go about implementing this for my brand?',
      }),
    ].filter(Boolean);
    askExpert(parts.join(' '));
  };
  return (
    <section id="fix" style={{ paddingTop: 44 }}>
      <SectionHead
        q="Q5"
        title={
          <FormattedMessage
            id="marketerView.q5fix.title"
            defaultMessage="What should I fix first?"
          />
        }
        sub={
          <FormattedMessage
            id="marketerView.q5fix.sub"
            defaultMessage="Ranked by leverage: biggest gap × widest knock-on effect. Start here; this is the single thing to move."
          />
        }
      />
      <LockedOverlay locked={locked}>
      {effectiveFix && (effectiveFix.title || effectiveFix.redline || effectiveFix.move) ? (
        <Card pad={0} style={{ overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr' }}>
            <div
              style={{
                background: BAND_PALETTE.weak.fg,
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px 0',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em' }}>
                THE
              </span>
              <span style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.05, letterSpacing: '0.04em' }}>
                FIX
              </span>
            </div>
            <div style={{ padding: '24px 28px' }}>
              {effectiveFix.title && (
                <h3
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#0F172A',
                    margin: 0,
                  }}
                >
                  {effectiveFix.title}
                </h3>
              )}
              {effectiveFix.redline && (
                <p
                  style={{
                    fontSize: 14.5,
                    color: BAND_PALETTE.weak.fg,
                    margin: '8px 0 0',
                    fontWeight: 500,
                  }}
                >
                  {effectiveFix.redline}
                </p>
              )}
              {effectiveFix.move && (
                <p style={{ fontSize: 15, lineHeight: 1.6, color: '#334155', margin: '16px 0 0' }}>
                  <b style={{ color: '#0F172A' }}>
                    <FormattedMessage id="marketerView.q5fix.move" defaultMessage="The move:" />
                  </b>{' '}
                  {effectiveFix.move}
                </p>
              )}
              {effectiveFix.expectedEffect && (
                <p style={{ fontSize: 15, lineHeight: 1.6, color: '#334155', margin: '12px 0 0' }}>
                  <b style={{ color: BAND_PALETTE.strong.fg }}>
                    <FormattedMessage
                      id="marketerView.q5fix.effect"
                      defaultMessage="↗ Expected effect:"
                    />
                  </b>{' '}
                  {effectiveFix.expectedEffect}
                </p>
              )}
              <div style={{ marginTop: 20 }}>
                <button
                  type="button"
                  onClick={handleAskExpert}
                  style={{
                    border: 'none',
                    background: THEME.primary,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    padding: '10px 20px',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  <FormattedMessage
                    id="marketerView.q5fix.askExpert"
                    defaultMessage="How can I do this? Ask the expert →"
                  />
                </button>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <p style={{ color: THEME.textSecondary, margin: 0 }}>
            <FormattedMessage
              id="marketerView.q5fix.pending"
              defaultMessage="The fix is being generated from your latest data, check back in a moment."
            />
          </p>
        </Card>
      )}
      </LockedOverlay>
    </section>
  );
};
